import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  ThumbsUp, ThumbsDown, Sparkles, MessageSquare, Send, 
  Settings, RefreshCw, TrendingUp, X, ChevronDown, ChevronUp,
  Brain, Zap, Target, Award, Users, Filter
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AISourcing = () => {
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [preferences, setPreferences] = useState({
    weights: {
      semantic_similarity: 0.4,
      skill_match: 0.3,
      experience_match: 0.2,
      location_match: 0.1
    }
  });
  const [aiInsights, setAiInsights] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [refining, setRefining] = useState(false);
  const [sourcing, setSourcing] = useState(false);
  const [sourcingStatus, setSourcingStatus] = useState(null);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [filterThreshold, setFilterThreshold] = useState(0.3);
  const [showOnlyQualified, setShowOnlyQualified] = useState(false);
  const [liveFeed, setLiveFeed] = useState([]);
  const [showLiveFeed, setShowLiveFeed] = useState(false);
  const [sourcingProgress, setSourcingProgress] = useState(0);
  const [eventSource, setEventSource] = useState(null);

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (selectedJobId) {
      fetchCandidatesForJob();
    }
  }, [selectedJobId]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      const openJobs = response.data.filter(job => job.status === 'open' || job.status === 'draft');
      setJobs(openJobs);
      if (openJobs.length > 0 && !selectedJobId) {
        setSelectedJobId(openJobs[0].id);
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
    }
  };

  const fetchCandidatesForJob = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/jobs/${selectedJobId}/matches?top_n=50`);
      setAllCandidates(response.data);
      
      // Apply filters
      if (showOnlyQualified) {
        const qualified = response.data.filter(c => c.match_score >= filterThreshold);
        setCandidates(qualified);
      } else {
        setCandidates(response.data);
      }
    } catch (error) {
      console.error("Error fetching candidates:", error);
    } finally {
      setLoading(false);
    }
  };

  const sourceCandidates = async (source) => {
    setSourcing(true);
    setShowLiveFeed(true);
    setLiveFeed([]);
    setSourcingProgress(0);
    setSourcingStatus({ status: 'running', message: 'Initializing candidate sourcing...', found: 0 });
    
    try {
      const selectedJob = jobs.find(j => j.id === selectedJobId);
      const searchQuery = `${selectedJob.title} ${selectedJob.required_skills?.slice(0, 3).join(' ') || ''}`;
      
      // Close any existing EventSource
      if (eventSource) {
        eventSource.close();
      }
      
      // Create Server-Sent Events connection for live feed
      const params = new URLSearchParams({
        source: source,
        search_query: searchQuery,
        location: selectedJob.location
      });
      
      const es = new EventSource(`${API}/scraping/start-stream/${selectedJobId}?${params}`);
      setEventSource(es);
      
      es.onopen = () => {
        console.log("Connected to sourcing stream");
      };
      
      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'status':
              setSourcingStatus({
                status: 'running',
                message: data.message,
                found: data.candidates_found || 0
              });
              setSourcingProgress(data.progress || 0);
              break;
              
            case 'candidate':
              // Add new candidate to live feed
              setLiveFeed(prev => [...prev, {
                ...data.data,
                id: data.data.candidate.id,
                timestamp: new Date()
              }]);
              setSourcingProgress(data.progress || 0);
              setSourcingStatus(prev => ({
                ...prev,
                found: data.candidates_found || prev.found
              }));
              break;
              
            case 'complete':
              setSourcingStatus({
                status: 'completed',
                message: data.message,
                found: data.candidates_found || 0
              });
              setSourcingProgress(100);
              
              // Refresh the main candidate list
              setTimeout(() => {
                fetchCandidatesForJob();
                setSourcingStatus(null);
                // Keep live feed visible for a few more seconds
                setTimeout(() => {
                  setShowLiveFeed(false);
                }, 3000);
              }, 2000);
              
              es.close();
              setSourcing(false);
              break;
              
            case 'error':
              setSourcingStatus({
                status: 'failed',
                message: data.message,
                found: 0
              });
              es.close();
              setSourcing(false);
              setTimeout(() => {
                setSourcingStatus(null);
                setShowLiveFeed(false);
              }, 3000);
              break;
          }
        } catch (error) {
          console.error("Error parsing SSE data:", error);
        }
      };
      
      es.onerror = (error) => {
        console.error("EventSource failed:", error);
        setSourcingStatus({
          status: 'failed',
          message: 'Connection to sourcing service failed. Please try again.',
          found: 0
        });
        es.close();
        setSourcing(false);
        setTimeout(() => {
          setSourcingStatus(null);
          setShowLiveFeed(false);
        }, 3000);
      };
      
      // The EventSource connection automatically starts the streaming process
      
    } catch (error) {
      console.error("Error starting candidate sourcing:", error);
      setSourcingStatus({ 
        status: 'failed', 
        message: 'Failed to start candidate sourcing. Please try again.',
        found: 0 
      });
      setSourcing(false);
      setTimeout(() => {
        setSourcingStatus(null);
        setShowLiveFeed(false);
      }, 3000);
    } finally {
      setShowSourceModal(false);
    }
  };

  // Cleanup EventSource on component unmount
  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [eventSource]);

  const toggleQualifiedFilter = () => {
    const newFilterState = !showOnlyQualified;
    setShowOnlyQualified(newFilterState);
    
    if (newFilterState) {
      const qualified = allCandidates.filter(c => c.match_score >= filterThreshold);
      setCandidates(qualified);
    } else {
      setCandidates(allCandidates);
    }
  };

  const handleFeedback = async (candidate, isPositive, reason = "") => {
    const feedback = {
      candidate_id: candidate.candidate.id,
      candidate_name: candidate.candidate.name,
      feedback: isPositive ? 'positive' : 'negative',
      reason: reason,
      timestamp: new Date().toISOString()
    };

    setFeedbackHistory([...feedbackHistory, feedback]);

    // Update candidate visual feedback
    setCandidates(candidates.map(c => 
      c.candidate.id === candidate.candidate.id 
        ? { ...c, userFeedback: isPositive ? 'liked' : 'disliked' }
        : c
    ));

    // Auto-refine after feedback
    if (feedbackHistory.length >= 2) {
      await refineSearch();
    }
  };

  const refineSearch = async () => {
    try {
      setRefining(true);
      const response = await axios.post(`${API}/ai/refine-search`, {
        job_id: selectedJobId,
        feedback_history: feedbackHistory,
        current_preferences: preferences
      });

      setAiInsights(response.data);
      
      // Update preferences with AI suggestions
      if (response.data.adjusted_weights) {
        setPreferences({
          ...preferences,
          weights: response.data.adjusted_weights
        });
      }

      // Re-fetch candidates with new criteria
      await fetchCandidatesForJob();
    } catch (error) {
      console.error("Error refining search:", error);
    } finally {
      setRefining(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;

    const userMessage = { role: 'user', content: chatInput };
    setChatMessages([...chatMessages, userMessage]);
    setChatInput("");

    try {
      const selectedJob = jobs.find(j => j.id === selectedJobId);
      const response = await axios.post(`${API}/ai/sourcing-chat`, {
        message: chatInput,
        context: {
          job_id: selectedJobId,
          job_title: selectedJob?.title,
          current_criteria: JSON.stringify(preferences),
          candidates_reviewed: candidates.length,
          feedback_count: feedbackHistory.length
        }
      });

      const aiMessage = { role: 'assistant', content: response.data.response };
      setChatMessages([...chatMessages, userMessage, aiMessage]);
    } catch (error) {
      console.error("Error sending message:", error);
      const errorMessage = { role: 'assistant', content: 'Sorry, I had trouble processing that.' };
      setChatMessages([...chatMessages, userMessage, errorMessage]);
    }
  };

  const selectedJob = jobs.find(j => j.id === selectedJobId);
  const likedCount = feedbackHistory.filter(f => f.feedback === 'positive').length;
  const dislikedCount = feedbackHistory.filter(f => f.feedback === 'negative').length;

  return (
    <div className="space-y-6" data-testid="ai-sourcing-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Brain className="h-8 w-8 mr-3 text-purple-600" />
            AI-Assisted Sourcing
          </h1>
          <p className="text-gray-600 mt-2">Human-in-the-loop candidate discovery with real-time AI learning</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSourceModal(true)}
            disabled={!selectedJobId || sourcing}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="source-candidates-button"
          >
            <Users className="h-4 w-4" />
            <span>Source New Candidates</span>
          </button>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center space-x-2"
          >
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </button>
          <button
            onClick={() => setShowChat(!showChat)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
          >
            <MessageSquare className="h-4 w-4" />
            <span>AI Assistant</span>
          </button>
        </div>
      </div>

      {/* Job Selector & Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl shadow-md p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Job to Source For:</label>
          <select
            value={selectedJobId || ''}
            onChange={(e) => setSelectedJobId(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-lg"
          >
            {jobs.map((job) => (
              <option key={job.id} value={job.id}>
                {job.title} - {job.department}
              </option>
            ))}
          </select>
          {selectedJob && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedJob.required_skills?.slice(0, 5).map((skill, idx) => (
                <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">
                  {skill}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-700 text-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Target className="h-5 w-5 mr-2" />
            Feedback Stats
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="flex items-center">
                <ThumbsUp className="h-4 w-4 mr-2" />
                Liked
              </span>
              <span className="text-2xl font-bold">{likedCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="flex items-center">
                <ThumbsDown className="h-4 w-4 mr-2" />
                Disliked
              </span>
              <span className="text-2xl font-bold">{dislikedCount}</span>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-purple-400">
              <span className="flex items-center">
                <TrendingUp className="h-4 w-4 mr-2" />
                Total Reviewed
              </span>
              <span className="text-2xl font-bold">{feedbackHistory.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* AI Insights Panel */}
      {aiInsights && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-purple-200 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <Sparkles className="h-6 w-6 text-purple-600 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-2">AI Insights & Recommendations</h3>
              <p className="text-gray-700 mb-4">{aiInsights.reasoning}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {aiInsights.emphasized_skills?.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-green-700 mb-2">✓ Emphasizing:</p>
                    <div className="flex flex-wrap gap-2">
                      {aiInsights.emphasized_skills.map((skill, idx) => (
                        <span key={idx} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {aiInsights.avoided_traits?.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-red-700 mb-2">✗ Avoiding:</p>
                    <div className="flex flex-wrap gap-2">
                      {aiInsights.avoided_traits.map((trait, idx) => (
                        <span key={idx} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-md">
                          {trait}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => setAiInsights(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Sourcing Status */}
      {sourcingStatus && (
        <div className={`rounded-xl p-4 border-2 ${
          sourcingStatus.status === 'completed' ? 'bg-green-50 border-green-300' :
          sourcingStatus.status === 'failed' ? 'bg-red-50 border-red-300' :
          'bg-blue-50 border-blue-300'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {sourcingStatus.status === 'running' && (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              )}
              {sourcingStatus.status === 'completed' && (
                <div className="h-5 w-5 bg-green-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">✓</span>
                </div>
              )}
              {sourcingStatus.status === 'failed' && (
                <div className="h-5 w-5 bg-red-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">✗</span>
                </div>
              )}
              <span className={`font-medium ${
                sourcingStatus.status === 'completed' ? 'text-green-800' :
                sourcingStatus.status === 'failed' ? 'text-red-800' :
                'text-blue-800'
              }`}>
                {sourcingStatus.message}
              </span>
            </div>
            
            {/* Progress Info */}
            <div className="text-right">
              <div className="text-sm font-semibold text-gray-700">
                {sourcingStatus.found || 0} candidates found
              </div>
              <div className="text-xs text-gray-500">
                {sourcingProgress}% complete
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          {sourcingStatus.status === 'running' && (
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${sourcingProgress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Live Candidate Feed */}
      {showLiveFeed && (
        <div className="bg-white rounded-xl shadow-md border-2 border-purple-300">
          <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-t-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="animate-pulse">
                  <Users className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Live Candidate Feed</h3>
                  <p className="text-sm text-purple-100">Candidates discovered in real-time</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold">{liveFeed.length}</div>
                <div className="text-xs text-purple-100">
                  {liveFeed.length > 50 ? 'Found (showing latest 50)' : 'Found'}
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {liveFeed.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto text-purple-300 mb-3" />
                <p className="text-gray-600">Searching for candidates...</p>
                <p className="text-sm text-gray-500 mt-1">New profiles will appear here as they're discovered</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {liveFeed.slice().reverse().slice(0, 50).map((candidateMatch, index) => (
                  <div 
                    key={`${candidateMatch.candidate.id}-${index}`}
                    className="flex items-center space-x-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 animate-fade-in"
                  >
                    {/* Match Score Circle */}
                    <div className="flex-shrink-0">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-white ${
                        candidateMatch.match_score >= 0.7 ? 'bg-green-500' :
                        candidateMatch.match_score >= 0.5 ? 'bg-yellow-500' :
                        'bg-gray-500'
                      }`}>
                        {Math.round(candidateMatch.match_score * 100)}%
                      </div>
                    </div>
                    
                    {/* Candidate Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-gray-900 truncate">
                        {candidateMatch.candidate.name}
                      </h4>
                      <p className="text-sm text-gray-600 truncate">
                        {candidateMatch.candidate.current_title} at {candidateMatch.candidate.current_company}
                      </p>
                      <p className="text-xs text-gray-500">
                        {candidateMatch.candidate.location} • {candidateMatch.candidate.experience_years} years exp
                      </p>
                      
                      {/* Top Skills */}
                      {candidateMatch.candidate.skills && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {candidateMatch.candidate.skills.slice(0, 3).map((skill, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                              {skill}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Source & Time */}
                    <div className="flex-shrink-0 text-right">
                      <div className="text-xs font-medium text-gray-700 capitalize">
                        {candidateMatch.candidate.source}
                      </div>
                      <div className="text-xs text-gray-500">
                        {candidateMatch.timestamp?.toLocaleTimeString() || 'Now'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Refine Button */}
      {feedbackHistory.length > 0 && (
        <div className="flex justify-center">
          <button
            onClick={refineSearch}
            disabled={refining}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-3 rounded-full font-semibold flex items-center space-x-2 shadow-lg disabled:opacity-50"
          >
            {refining ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>AI is Learning...</span>
              </>
            ) : (
              <>
                <Zap className="h-5 w-5" />
                <span>Refine Search with AI</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Candidates List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-600">Finding best matches...</p>
        </div>
      ) : candidates.length === 0 && allCandidates.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Brain className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No candidates yet</h3>
          <p className="text-gray-600 mb-6">Source candidates for this job to start AI-assisted matching</p>
          <button
            onClick={() => setShowSourceModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg inline-flex items-center space-x-2"
          >
            <Users className="h-5 w-5" />
            <span>Source Candidates Now</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Filter Controls */}
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  AI-Ranked Candidates ({candidates.length})
                </h2>
                {showOnlyQualified && (
                  <p className="text-sm text-gray-600 mt-1">
                    Showing qualified candidates (≥{(filterThreshold * 100).toFixed(0)}% match)
                  </p>
                )}
              </div>
              
              <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
                {/* Match Threshold Slider */}
                <div className="flex items-center space-x-3 bg-gray-50 px-4 py-2 rounded-lg">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Min Match:
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={filterThreshold}
                    onChange={(e) => {
                      setFilterThreshold(parseFloat(e.target.value));
                      if (showOnlyQualified) {
                        const qualified = allCandidates.filter(c => c.match_score >= parseFloat(e.target.value));
                        setCandidates(qualified);
                      }
                    }}
                    className="w-24"
                  />
                  <span className="text-sm font-semibold text-purple-600 w-12">
                    {(filterThreshold * 100).toFixed(0)}%
                  </span>
                </div>

                {/* Toggle Filter */}
                <button
                  onClick={toggleQualifiedFilter}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-all ${
                    showOnlyQualified 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  data-testid="toggle-qualified-filter"
                >
                  <Filter className="h-4 w-4" />
                  <span>{showOnlyQualified ? 'Show All' : 'Show Qualified Only'}</span>
                </button>
              </div>
            </div>

            {/* Filter Info */}
            {showOnlyQualified && candidates.length === 0 && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  No candidates meet the {(filterThreshold * 100).toFixed(0)}% match threshold. 
                  Try lowering the threshold or sourcing more candidates.
                </p>
              </div>
            )}
          </div>

          {/* Candidate Cards */}
          {candidates.length > 0 && (
            <div className="grid grid-cols-1 gap-4">
              {candidates.map((match, index) => (
                <CandidateCard
                  key={match.candidate.id}
                  match={match}
                  rank={index + 1}
                  onFeedback={handleFeedback}
                  userFeedback={match.userFeedback}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <SettingsPanel
          preferences={preferences}
          onUpdate={setPreferences}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* AI Chat Assistant */}
      {showChat && (
        <ChatAssistant
          messages={chatMessages}
          input={chatInput}
          onInputChange={setChatInput}
          onSend={sendChatMessage}
          onClose={() => setShowChat(false)}
        />
      )}

      {/* Source Candidates Modal */}
      {showSourceModal && (
        <SourceCandidatesModal
          job={jobs.find(j => j.id === selectedJobId)}
          onSource={sourceCandidates}
          onClose={() => setShowSourceModal(false)}
          sourcing={sourcing}
        />
      )}
    </div>
  );
};

const SourceCandidatesModal = ({ job, onSource, onClose, sourcing }) => {
  const [selectedSource, setSelectedSource] = useState('all');

  const sources = [
    { id: 'linkedin', name: 'LinkedIn', icon: '💼', desc: 'Professional network' },
    { id: 'indeed', name: 'Indeed', icon: '🔍', desc: 'Job board database' },
    { id: 'monster', name: 'Monster', icon: '👾', desc: 'Recruiting platform' },
    { id: 'all', name: 'All Sources', icon: '🌐', desc: 'Search everywhere', recommended: true }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
        <div className="px-6 py-4 border-b bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-t-xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold flex items-center">
                <Users className="h-6 w-6 mr-2" />
                Source New Candidates
              </h2>
              <p className="text-sm text-green-100 mt-1">
                AI-powered candidate discovery from top job boards
              </p>
            </div>
            <button onClick={onClose} disabled={sourcing}>
              <X className="h-6 w-6 hover:text-gray-200" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Job Info */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border-2 border-purple-200">
            <p className="text-sm text-gray-600 mb-1">Sourcing for:</p>
            <h3 className="text-lg font-bold text-gray-900">{job?.title}</h3>
            <p className="text-sm text-gray-600">{job?.department} • {job?.location}</p>
            {job?.required_skills && job.required_skills.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {job.required_skills.slice(0, 5).map((skill, idx) => (
                  <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-md">
                    {skill}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Source Selection */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">Select Source:</p>
            <div className="grid grid-cols-2 gap-3">
              {sources.map((source) => (
                <button
                  key={source.id}
                  onClick={() => setSelectedSource(source.id)}
                  disabled={sourcing}
                  className={`relative p-4 rounded-lg border-2 transition-all ${
                    selectedSource === source.id
                      ? 'border-green-500 bg-green-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  } ${sourcing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {source.recommended && (
                    <div className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-full">
                      Best
                    </div>
                  )}
                  <div className="text-3xl mb-2">{source.icon}</div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">{source.name}</p>
                    <p className="text-xs text-gray-600">{source.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* AI Enhancement Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Sparkles className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">AI-Enhanced Sourcing</p>
                <p className="text-xs text-blue-700 mt-1">
                  Our AI will automatically match and rank candidates based on your job requirements. 
                  Candidates are added to your pipeline and ready for review.
                </p>
              </div>
            </div>
          </div>

          {/* Expected Results */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Expected candidates:</span>
            <span className="font-bold text-gray-900">
              {selectedSource === 'all' ? '100-150' : '50-100'} profiles
            </span>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={sourcing}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onSource(selectedSource)}
            disabled={sourcing || !selectedSource}
            className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white px-6 py-2 rounded-lg font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sourcing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Sourcing...</span>
              </>
            ) : (
              <>
                <Users className="h-4 w-4" />
                <span>Start Sourcing</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const CandidateCard = ({ match, rank, onFeedback, userFeedback }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState("");
  const [showFeedbackInput, setShowFeedbackInput] = useState(null);

  const candidate = match.candidate;
  const scores = match.score_breakdown;
  const totalScore = match.match_score;

  const getFeedbackColor = () => {
    if (userFeedback === 'liked') return 'border-green-500 bg-green-50';
    if (userFeedback === 'disliked') return 'border-red-500 bg-red-50';
    return 'border-gray-200';
  };

  return (
    <div className={`bg-white rounded-xl shadow-md p-6 border-2 ${getFeedbackColor()} transition-all`}>
      <div className="flex items-start justify-between">
        {/* Rank Badge */}
        <div className="flex items-start space-x-4">
          <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
            rank <= 3 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white' : 'bg-gray-200 text-gray-700'
          }`}>
            #{rank}
          </div>

          {/* Candidate Info */}
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-xl font-bold text-gray-900">{candidate.name}</h3>
              {rank <= 3 && <Award className="h-5 w-5 text-yellow-500" />}
            </div>
            <p className="text-gray-600">{candidate.current_title || 'N/A'}</p>
            <p className="text-sm text-gray-500">{candidate.location} • {candidate.experience_years} years exp</p>
            
            {/* Match Score */}
            <div className="mt-4 flex items-center space-x-2">
              <div className="flex-1 bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all"
                  style={{ width: `${totalScore * 100}%` }}
                />
              </div>
              <span className="text-sm font-bold text-purple-700">{(totalScore * 100).toFixed(0)}% Match</span>
            </div>

            {/* Skills */}
            {candidate.skills && candidate.skills.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {candidate.skills.slice(0, 5).map((skill, idx) => (
                  <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-md">
                    {skill}
                  </span>
                ))}
                {candidate.skills.length > 5 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
                    +{candidate.skills.length - 5}
                  </span>
                )}
              </div>
            )}

            {/* Score Breakdown - Collapsible */}
            {showDetails && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-2">
                <p className="text-sm font-semibold text-gray-700 mb-2">Score Breakdown:</p>
                <ScoreBar label="Semantic Match" score={scores.semantic_similarity} />
                <ScoreBar label="Skill Match" score={scores.skill_match} />
                <ScoreBar label="Experience Match" score={scores.experience_match} />
                <ScoreBar label="Location Match" score={scores.location_match} />
              </div>
            )}

            <button
              onClick={() => setShowDetails(!showDetails)}
              className="mt-3 text-sm text-purple-600 hover:text-purple-700 flex items-center space-x-1"
            >
              <span>{showDetails ? 'Hide' : 'Show'} Details</span>
              {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Feedback Buttons */}
        {!userFeedback && (
          <div className="flex flex-col gap-2">
            <button
              onClick={() => setShowFeedbackInput('positive')}
              className="bg-green-500 hover:bg-green-600 text-white p-3 rounded-lg transition-all hover:scale-110"
              title="Like this candidate"
            >
              <ThumbsUp className="h-5 w-5" />
            </button>
            <button
              onClick={() => setShowFeedbackInput('negative')}
              className="bg-red-500 hover:bg-red-600 text-white p-3 rounded-lg transition-all hover:scale-110"
              title="Dislike this candidate"
            >
              <ThumbsDown className="h-5 w-5" />
            </button>
          </div>
        )}

        {userFeedback && (
          <div className="flex items-center space-x-2 text-sm font-medium">
            {userFeedback === 'liked' ? (
              <>
                <ThumbsUp className="h-5 w-5 text-green-600" />
                <span className="text-green-700">Liked</span>
              </>
            ) : (
              <>
                <ThumbsDown className="h-5 w-5 text-red-600" />
                <span className="text-red-700">Disliked</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Feedback Input */}
      {showFeedbackInput && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border-2 border-purple-300">
          <p className="text-sm font-medium text-gray-700 mb-2">
            {showFeedbackInput === 'positive' ? 'What do you like?' : 'Why not a good fit?'} (optional)
          </p>
          <textarea
            value={feedbackReason}
            onChange={(e) => setFeedbackReason(e.target.value)}
            placeholder="e.g., Strong React experience, Good culture fit, etc."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2"
            rows={2}
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setShowFeedbackInput(null);
                setFeedbackReason("");
              }}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onFeedback(match, showFeedbackInput === 'positive', feedbackReason);
                setShowFeedbackInput(null);
                setFeedbackReason("");
              }}
              className="px-4 py-1 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg"
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ScoreBar = ({ label, score }) => {
  const percentage = (score * 100).toFixed(0);
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-semibold text-gray-900">{percentage}%</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full">
        <div
          className="h-full bg-purple-600 rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const SettingsPanel = ({ preferences, onUpdate, onClose }) => {
  const [localPrefs, setLocalPrefs] = useState(preferences);

  const updateWeight = (key, value) => {
    setLocalPrefs({
      ...localPrefs,
      weights: {
        ...localPrefs.weights,
        [key]: parseFloat(value)
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Matching Preferences</h2>
          <button onClick={onClose}>
            <X className="h-6 w-6 text-gray-400 hover:text-gray-600" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          <p className="text-sm text-gray-600">Adjust how candidates are ranked (weights must sum to ~1.0)</p>
          
          {Object.entries(localPrefs.weights).map(([key, value]) => (
            <div key={key}>
              <div className="flex justify-between text-sm mb-2">
                <label className="font-medium text-gray-700 capitalize">
                  {key.replace(/_/g, ' ')}
                </label>
                <span className="text-purple-600 font-semibold">{(value * 100).toFixed(0)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={value}
                onChange={(e) => updateWeight(key, e.target.value)}
                className="w-full"
              />
            </div>
          ))}
        </div>

        <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onUpdate(localPrefs);
              onClose();
            }}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
          >
            Apply Changes
          </button>
        </div>
      </div>
    </div>
  );
};

const ChatAssistant = ({ messages, input, onInputChange, onSend, onClose }) => {
  return (
    <div className="fixed bottom-4 right-4 w-96 bg-white rounded-xl shadow-2xl border-2 border-purple-300 z-50">
      <div className="px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-t-xl flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Brain className="h-5 w-5" />
          <span className="font-semibold">AI Sourcing Assistant</span>
        </div>
        <button onClick={onClose}>
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="h-96 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 text-sm py-8">
            <Brain className="h-12 w-12 mx-auto mb-3 text-purple-300" />
            <p>Ask me anything about:</p>
            <ul className="mt-2 text-left inline-block">
              <li>• Refining your search</li>
              <li>• Understanding candidates</li>
              <li>• Adjusting criteria</li>
              <li>• Best practices</li>
            </ul>
          </div>
        )}
        
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-4 py-2 rounded-lg ${
              msg.role === 'user' 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-100 text-gray-900'
            }`}>
              <p className="text-sm">{msg.content}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 border-t">
        <div className="flex space-x-2">
          <input
            type="text"
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && onSend()}
            placeholder="Ask AI assistant..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
          />
          <button
            onClick={onSend}
            className="bg-purple-600 hover:bg-purple-700 text-white p-2 rounded-lg"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AISourcing;
